import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SERVICES, type RequestStatus } from '@shared';
import { useAuth } from '../../lib/auth-context';
import { apiGet } from '../../lib/api';

interface RequestRow { id: string; status: RequestStatus; service: string; headline: string; created_at: string }
interface InvoiceRow { id: string; status: string; amount_pence: number; currency: string; due_at: string }
interface DocumentRow { id: string }
interface ClientRow { id: string; kyc: string; name: string }
// Shape of GET /api/portal/kyc/status — only the fields the dashboard needs to
// show upload progress. `by_category[cat].state` is 'missing' until a doc is
// uploaded, then 'uploaded' → 'in_review' → 'accepted' (or 'rejected').
interface KycStatusRow {
  required: string[];
  by_category: Record<string, { state: string }>;
}

const OUTSTANDING_STATUSES = new Set(['issued', 'awaiting_wire']);
const OPEN_REQUEST_STATUSES = new Set<RequestStatus>(['submitted', 'reviewing', 'engagement']);

const REQUEST_LABEL: Record<RequestStatus, string> = {
  submitted: 'Submitted',
  reviewing: 'In review',
  engagement: 'Active engagement',
  delivered: 'Delivered',
  closed: 'Closed',
};

const REQUEST_TONE: Record<RequestStatus, string> = {
  submitted: 'chip',
  reviewing: 'chip accent',
  engagement: 'chip accent',
  delivered: 'chip green',
  closed: 'chip',
};

// Canonical KYC states per src/shared/client.ts:
//   pending  — no documents collected yet (the default for new clients)
//   current  — verified and in date
//   refresh  — verified but due for refresh
//   expired  — lapsed
//   rejected — submitted and not accepted
function kycLabel(s: string): string {
  switch (s) {
    case 'pending':   return 'Documents needed';
    case 'in_review': return 'In progress';
    case 'current':   return 'Verified';
    case 'refresh':   return 'Refresh due';
    case 'rejected':  return 'Rejected';
    case 'expired':   return 'Renewal required';
    default:          return s;
  }
}

function kycChipClass(s: string): string {
  if (s === 'current') return 'chip green';
  if (s === 'rejected' || s === 'expired') return 'chip red';
  if (s === 'in_review') return 'chip accent';
  if (s === 'refresh' || s === 'pending') return 'chip';
  return 'chip';
}

// ── Component ──────────────────────────────────────────────────────────────

export function PortalDashboard() {
  const { user } = useAuth();
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [client, setClient] = useState<ClientRow | null>(null);
  const [kycStatus, setKycStatus] = useState<KycStatusRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.client_id) { setLoading(false); return; }
      try {
        const [r, i, d, cl, k] = await Promise.all([
          apiGet<{ requests: RequestRow[] }>('/api/requests'),
          apiGet<{ invoices: InvoiceRow[] }>('/api/invoices'),
          apiGet<{ documents: DocumentRow[] }>('/api/documents'),
          apiGet<{ client: ClientRow }>(`/api/clients/${user.client_id}`),
          apiGet<KycStatusRow>('/api/portal/kyc/status').catch(() => null),
        ]);
        if (cancelled) return;
        setRequests(r.requests);
        setInvoices(i.invoices);
        setDocumentsCount(d.documents.length);
        setClient(cl.client);
        setKycStatus(k);
      } catch {
        // Soft-fail: dashboard shows skeletons rather than crashing the shell.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user?.client_id]);

  const openRequests = requests.filter((r) => OPEN_REQUEST_STATUSES.has(r.status));
  const outstanding = invoices.filter((i) => OUTSTANDING_STATUSES.has(i.status));

  // KYC upload progress — how many required categories the client has started
  // (uploaded / in review / accepted). Drives the dashboard card so a partial
  // upload is acknowledged instead of a blunt "Documents needed".
  // ── Dashboard KYC label ────────────────────────────────────────────────────
  // Product rule: uploading every required category is NOT mandatory (e.g.
  // commission-basis introducers provide far fewer documents). The dashboard
  // label is therefore driven by the *state of the documents the client has
  // provided*, not by full coverage of the required set:
  //   • all provided docs approved (>=1)         → "Verified"
  //   • any still uploading / under admin review → "In progress"
  //   • any rejected                             → "Rejected"
  //   • nothing provided                         → "Documents needed"
  // The server-side rollup (client.kyc) stays stricter and still governs any
  // back-office gating — this is the client-facing dashboard label only.
  const kycRequired = kycStatus?.required ?? [];
  const kycTotal = kycRequired.length;
  const kycStateOf = (cat: string) => kycStatus?.by_category?.[cat]?.state;
  const kycAccepted = kycRequired.filter((c) => kycStateOf(c) === 'accepted').length;
  const kycInReview = kycRequired.filter((c) => kycStateOf(c) === 'in_review').length;
  const kycUploaded = kycRequired.filter((c) => kycStateOf(c) === 'uploaded').length;
  const kycRejected = kycRequired.filter((c) => kycStateOf(c) === 'rejected').length;
  const kycProvided = kycAccepted + kycInReview + kycUploaded;

  const kycProgressLink = <Link to="/portal/compliance">{kycProvided} of {kycTotal} provided →</Link>;
  let kycText: string;
  let kycCls: string;
  let kycDetail: ReactNode;
  if (!client) {
    kycText = '—'; kycCls = 'chip'; kycDetail = null;
  } else if (kycTotal === 0) {
    // Per-category status didn't load — fall back to the server rollup label.
    kycText = kycLabel(client.kyc);
    kycCls = kycChipClass(client.kyc);
    kycDetail = client.kyc === 'pending' ? 'Required before any engagement.' : null;
  } else if (client.kyc === 'expired') {
    kycText = 'Renewal required'; kycCls = 'chip red'; kycDetail = 'Resubmission required.';
  } else if (client.kyc === 'refresh') {
    kycText = 'Refresh due'; kycCls = 'chip'; kycDetail = 'Refresh due soon.';
  } else if (kycRejected > 0) {
    kycText = 'Rejected'; kycCls = 'chip red'; kycDetail = 'Resubmission required.';
  } else if (kycInReview > 0 || kycUploaded > 0) {
    kycText = 'In progress'; kycCls = 'chip accent'; kycDetail = kycProgressLink;
  } else if (kycAccepted > 0) {
    kycText = 'Verified'; kycCls = 'chip green';
    kycDetail = `${kycAccepted} document${kycAccepted === 1 ? '' : 's'} approved.`;
  } else {
    kycText = 'Documents needed'; kycCls = 'chip'; kycDetail = kycProgressLink;
  }

  return (
    <>
      <div>
        <h1 className="portal-h1">Welcome, {user?.name?.split(' ')[0] ?? 'there'}.</h1>
        <p className="portal-sub">Here's where things stand on {today}.</p>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Briefs in motion</div>
          <div className="val">
            {loading ? <span className="skeleton skeleton-num" /> : openRequests.length}
          </div>
          <div className="delta">
            {loading ? <span className="skeleton skeleton-line" />
              : openRequests.length === 0 ? 'All briefs closed — start one when ready.'
              : <Link to="/portal/requests">View all →</Link>}
          </div>
        </div>
        <div className="card">
          <div className="lbl">Outstanding</div>
          <div className="val">
            {loading ? <span className="skeleton skeleton-num" /> : outstanding.length}
          </div>
          <div className="delta">
            {loading ? <span className="skeleton skeleton-line" />
              : outstanding.length === 0 ? 'No invoices due'
              : <Link to="/portal/invoices">View invoices →</Link>}
          </div>
        </div>
        <div className="card">
          <div className="lbl">Deliverables</div>
          <div className="val">
            {loading ? <span className="skeleton skeleton-num" /> : documentsCount}
          </div>
          <div className="delta">
            {loading ? <span className="skeleton skeleton-line" />
              : documentsCount === 0 ? 'None yet'
              : <Link to="/portal/documents">View documents →</Link>}
          </div>
        </div>
        <div className="card">
          <div className="lbl">KYC Compliance</div>
          <div className="val" style={{ fontSize: 22 }}>
            {loading ? <span className="skeleton skeleton-num" />
              : <span className={kycCls}>{kycText}</span>}
          </div>
          <div className="delta">
            {loading ? <span className="skeleton skeleton-line" /> : kycDetail}
          </div>
        </div>
      </div>

      {!loading && openRequests.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 15, margin: 0, marginBottom: 12 }}>Open briefs</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {openRequests.slice(0, 5).map((r) => (
              <Link
                key={r.id}
                to={`/portal/requests/${r.id}`}
                style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line)', textDecoration: 'none', color: 'inherit' }}
              >
                <div>
                  <div style={{ fontSize: 13, color: 'var(--fg)' }}>{r.headline}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {r.id} · {SERVICES.find((s) => s.id === r.service)?.name ?? r.service}
                  </div>
                </div>
                <span className={REQUEST_TONE[r.status]} style={{ alignSelf: 'center' }}>
                  {REQUEST_LABEL[r.status]}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {!loading && requests.length === 0 && (
        <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: '32px 16px' }}>
          <h3 style={{ fontSize: 15, margin: 0 }}>No briefs yet</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
            Open your first brief to introduce a scope to your engagement lead.
            Average time to first proposal: 2 UK business days.
          </p>
          <Link className="btn accent" to="/portal/requests/new" style={{ marginTop: 12, display: 'inline-flex' }}>
            + New brief
          </Link>
        </div>
      )}
    </>
  );
}
